import React, { useState, useEffect } from "react";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import useTabMusic from "@/hooks/useTabMusic";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Coins, FlaskConical, Shield, Users, Sparkles, Sword, Heart, Loader2, Hourglass } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import soundManager from "@/utils/SoundManager";
import { t } from "@/utils/i18n";
import Packs from "./Packs";

export default function Shop() {
  const { game, setGame } = useGame();
  const { token, API } = useAuth();
  const [shopItems, setShopItems] = useState([]);
  const [goldPacks, setGoldPacks] = useState([]);
  const [activeTab, setActiveTab] = useState("potions");
  const [buying, setBuying] = useState(null);
  const [buyingPack, setBuyingPack] = useState(null);
  useTabMusic('shop');

  const lang = game?.language || "en";
  const gold = game?.character?.gold || 0;

  useEffect(() => {
    const loadShop = async () => {
      try {
        const [shopRes, packsRes] = await Promise.all([
          axios.get(`${API}/shop`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/gold-packs`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setShopItems(shopRes.data);
        setGoldPacks(packsRes.data);
      } catch {}
    };
    loadShop();
  }, [API, token]);

  const handleBuy = async (itemId) => {
    if (!game?.id || buying) return;
    setBuying(itemId);
    try {
      const res = await axios.post(`${API}/game/${game.id}/shop/buy`, { item_id: itemId }, { headers: { Authorization: `Bearer ${token}` } });
      setGame(res.data.game);
      soundManager.playNewItem();
      toast.success(t(lang, 'bought'));
    } catch (e) {
      const detail = e.response?.data?.detail || "Purchase failed";
      if (detail.includes("gold")) {
        toast.error(t(lang, 'not_enough_gold'));
      } else {
        toast.error(detail);
      }
    } finally {
      setBuying(null);
    }
  };

  const handleBuyGoldPack = async (packId) => {
    if (buyingPack) return;
    setBuyingPack(packId);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/checkout/create`, {
        pack_id: packId,
        origin_url: `${origin}${window.location.pathname}`
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBuyingPack(null);
    }
  };

  const getLocalizedItem = (item) => {
    const i18n = item.i18n || {};
    if (lang !== "en" && i18n[lang]) {
      return { ...item, name: i18n[lang][0], description: i18n[lang][1] };
    }
    return item;
  };

  // Format remaining window for seasonal items
  const formatRemaining = (untilIso) => {
    if (!untilIso) return null;
    const diffMs = new Date(untilIso).getTime() - Date.now();
    if (diffMs <= 0) return lang === "it" ? "Scaduto" : "Expired";
    const days = Math.floor(diffMs / 86400000);
    const hours = Math.floor((diffMs % 86400000) / 3600000);
    if (days >= 1) return lang === "it" ? `${days}g ${hours}h` : `${days}d ${hours}h`;
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    return lang === "it" ? `${hours}h ${minutes}m` : `${hours}h ${minutes}m`;
  };

  const categories = [
    { id: "potions", label: t(lang, 'shop_potions'), icon: FlaskConical },
    { id: "equipment", label: t(lang, 'shop_equipment'), icon: Shield },
    { id: "allies", label: t(lang, 'shop_allies'), icon: Users },
    { id: "abilities", label: t(lang, 'shop_abilities'), icon: Sparkles },
  ];

  const filteredItems = shopItems.filter(i => i.category === activeTab);

  if (!game) return null;

  return (
    <div className="py-3 sm:py-6 px-2 sm:px-4" data-testid="shop-view">
      <div className="max-w-4xl mx-auto">
        {/* Gold bar */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="font-heading text-base sm:text-xl text-[#cfb53b] flex items-center gap-2">
            <Coins size={18} /> {t(lang, 'shop')}
          </h2>
          <div className="flex items-center gap-2 bg-[#cfb53b]/10 border border-[#cfb53b]/30 rounded-full px-4 py-1.5" data-testid="shop-gold">
            <Coins size={14} className="text-[#cfb53b]" />
            <span className="font-heading text-sm text-[#cfb53b]">{gold}</span>
            <span className="text-xs text-[#94a3b8]">{t(lang, 'gold')}</span>
          </div>
        </div>

        {/* Card Packs (Dust-purchasable) */}
        <Packs />

        {/* Gold Packs - Real Money (HIDDEN: Stripe gold purchase disabled for now) */}
        {false && goldPacks.length > 0 && (
          <div className="mb-4 sm:mb-6 bg-gradient-to-r from-[#cfb53b]/5 to-[#cfb53b]/10 border border-[#cfb53b]/25 rounded-lg p-3 sm:p-4" data-testid="gold-packs-section">
            <h3 className="font-heading text-sm text-[#cfb53b] mb-3 flex items-center gap-2">
              <Coins size={16} /> {t(lang, 'gold_packs')}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              {goldPacks.map(pack => (
                <div key={pack.id} className="bg-[#0a0a0c]/80 border border-[#cfb53b]/20 rounded-lg p-3 text-center hover:border-[#cfb53b]/50 transition-all" data-testid={`gold-pack-${pack.id}`}>
                  <Coins size={24} className="mx-auto text-[#cfb53b] mb-1.5" />
                  <p className="font-heading text-sm text-[#cfb53b]">{pack.gold}</p>
                  <p className="text-[10px] text-[#94a3b8] mb-2">{t(lang, 'gold')}</p>
                  <Button
                    data-testid={`buy-pack-${pack.id}`}
                    onClick={() => handleBuyGoldPack(pack.id)}
                    disabled={buyingPack === pack.id}
                    className="w-full bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading text-xs h-7"
                    size="sm"
                  >
                    {buyingPack === pack.id ? <Loader2 size={12} className="animate-spin" /> : `$${pack.price}`}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#1a1a1d] border border-[#cfb53b]/20 w-full grid grid-cols-4">
            {categories.map(cat => {
              const Icon = cat.icon;
              return (
                <TabsTrigger
                  key={cat.id}
                  value={cat.id}
                  data-testid={`shop-tab-${cat.id}`}
                  className="font-heading text-xs sm:text-sm data-[state=active]:bg-[#cfb53b]/10 data-[state=active]:text-[#cfb53b]"
                >
                  <Icon size={14} className="mr-1 hidden sm:inline" />
                  {cat.label}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {categories.map(cat => (
            <TabsContent key={cat.id} value={cat.id} className="mt-4">
              {filteredItems.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-[#2d3748] rounded-lg">
                  <cat.icon size={40} className="mx-auto text-[#cfb53b]/20 mb-3" />
                  <p className="text-[#94a3b8] text-sm">{t(lang, 'shop_empty')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {filteredItems.map(rawItem => {
                    const item = getLocalizedItem(rawItem);
                    const canAfford = gold >= item.price;
                    const isBuying = buying === item.id;

                    return (
                      <div
                        key={item.id}
                        data-testid={`shop-item-${item.id}`}
                        className={`bg-[#1a1a1d]/80 border rounded-lg p-4 transition-all ${
                          canAfford ? 'border-[#cfb53b]/25 hover:border-[#cfb53b]/50' : 'border-[#2d3748] opacity-60'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-heading text-sm text-[#e2e8f0]">{item.name}</h4>
                            {item.is_seasonal && (
                              <div className="flex items-center gap-1 mt-1 text-[9px] text-orange-400 bg-orange-400/10 border border-orange-400/30 rounded-full px-2 py-0.5 w-fit" data-testid={`seasonal-badge-${item.id}`} title={item.available_until ? `Until ${item.available_until}` : ''}>
                                <Hourglass size={9} />
                                <span className="font-heading">{lang === "it" ? "Limitato" : "Limited"}</span>
                                {item.available_until && (
                                  <span className="text-orange-300">· {formatRemaining(item.available_until)}</span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0 ml-2">
                            <Coins size={12} className="text-[#cfb53b]" />
                            <span className="font-heading text-sm text-[#cfb53b]">{item.price}</span>
                          </div>
                        </div>

                        <p className="text-xs text-[#94a3b8] mb-3 leading-relaxed">{item.description}</p>

                        {/* Stats */}
                        <div className="flex flex-wrap gap-1.5 mb-3 text-[10px]">
                          {item.attack > 0 && (
                            <span className="flex items-center gap-0.5 text-red-400 bg-red-400/10 px-1.5 py-0.5 rounded"><Sword size={8} /> +{item.attack}</span>
                          )}
                          {item.defense > 0 && (
                            <span className="flex items-center gap-0.5 text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded"><Shield size={8} /> +{item.defense}</span>
                          )}
                          {item.hp > 0 && (
                            <span className="flex items-center gap-0.5 text-green-400 bg-green-400/10 px-1.5 py-0.5 rounded"><Heart size={8} /> {item.hp}</span>
                          )}
                          {item.effect === "damage" && (
                            <span className="text-red-400 bg-red-400/10 px-1.5 py-0.5 rounded">DMG {item.effect_value}</span>
                          )}
                          {item.effect === "heal" && (
                            <span className="text-green-400 bg-green-400/10 px-1.5 py-0.5 rounded">HEAL {item.effect_value}</span>
                          )}
                          {item.effect === "defense" && (
                            <span className="text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded">DEF +{item.effect_value}</span>
                          )}
                          {item.mana_cost > 0 && item.type !== "object" && (
                            <span className="text-blue-300 bg-blue-300/10 px-1.5 py-0.5 rounded">{t(lang, 'mana')}: {item.mana_cost}</span>
                          )}
                        </div>

                        <Button
                          data-testid={`buy-${item.id}`}
                          onClick={() => handleBuy(item.id)}
                          disabled={!canAfford || isBuying}
                          className={`w-full text-xs font-heading ${
                            canAfford
                              ? 'bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c]'
                              : 'bg-[#2d3748] text-[#64748b] cursor-not-allowed'
                          }`}
                          size="sm"
                        >
                          {isBuying ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <>{t(lang, 'buy')} - {item.price} {t(lang, 'gold')}</>
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
